import { isMac } from '../../settings';

/**
 * One key combination, as a shortcut declares it.
 *
 * `key` is the `KeyboardEvent.key` value: 'l', ' ', 'Escape', 'Enter',
 * 'ArrowRight', '?', '/', 'Delete'. Letters match either case, so caps lock
 * changes nothing. `mod` is *the platform's* modifier - ⌘ on a Mac and ctrl
 * everywhere else, never the other one - which is the rule the app already
 * uses for ctrl-click and ctrl+a.
 */
export interface KeyBinding {
  key: string;
  /** Other `key` values that mean the same: 'Backspace' next to 'Delete'. */
  aliases?: string[];
  /** ⌘ on a Mac, ctrl elsewhere. */
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /**
   * A two-key chord: this key pressed shortly after the prefix key, as in
   * "g then y". The prefix on its own is swallowed.
   */
  prefix?: string;
}

/** The platform's modifier and only that one. */
export function platformModifier(ev: KeyboardEvent) {
  return isMac ? ev.metaKey && !ev.ctrlKey : ev.ctrlKey && !ev.metaKey;
}

function sameKey(binding: KeyBinding, key: string) {
  return [binding.key, ...(binding.aliases || [])].some((wanted) =>
    wanted.length === 1 ? wanted.toLowerCase() === key.toLowerCase() : wanted === key,
  );
}

/**
 * Punctuation is reached through shift on some layouts and not others - '?'
 * is shift+/ on a US keyboard, '/' itself is shift+7 on an Icelandic one -
 * so for those keys the shift state says nothing and is not checked.
 */
function shiftAgnostic(key: string) {
  return key.length === 1 && key !== ' ' && !/[a-z]/i.test(key);
}

/** Whether this keydown is this binding, modifiers included. */
export function matches(binding: KeyBinding, ev: KeyboardEvent) {
  if (!sameKey(binding, ev.key)) {
    return false;
  }

  const wantMod = !!binding.mod;

  if (wantMod !== platformModifier(ev)) {
    return false;
  }

  if (!wantMod && (ev.ctrlKey || ev.metaKey)) {
    return false;
  }

  if (!!binding.alt !== ev.altKey) {
    return false;
  }

  if (!shiftAgnostic(binding.key) && !!binding.shift !== ev.shiftKey) {
    return false;
  }

  return true;
}

/**
 * Whether the user is writing in this element, in which case a key without
 * a modifier is theirs. Every input, not only the text ones: space ticks a
 * checkbox and opens a select. The audio element is deliberately *not* here
 * - see usePlayPauseShortcut.
 */
export function isTyping(target: EventTarget | null | undefined) {
  const el = target as HTMLElement | null | undefined;

  if (!el || typeof el.tagName !== 'string') {
    return false;
  }

  switch (el.tagName) {
    case 'INPUT':
    case 'TEXTAREA':
    case 'SELECT':
      return true;

    default:
      return el.isContentEditable === true;
  }
}

/**
 * Both the event's target and the active element are asked, because a key
 * that reaches `document` through a component that stops propagation
 * elsewhere is not worth relying on for this.
 */
export function isTypingIn(ev: KeyboardEvent) {
  return isTyping(ev.target) || isTyping(document.activeElement);
}

/**
 * Something keyboard-operable has focus - a button, a link, a sidebar row
 * that takes Enter - so Enter is its and not the list's. Track rows are not
 * focusable, so after a click on one the focus is on the body and Enter is
 * free; after a click on a row's play button it is on that button, which
 * Enter would press again.
 */
export function focusIsOnControl() {
  const el = document.activeElement as HTMLElement | null;

  return !!el && el !== document.body && el.tabIndex >= 0;
}

/**
 * A react-contextmenu menu is open. The library handles Enter, Escape and
 * the arrows itself while one is showing, and this is the class it marks
 * the open one with.
 */
export function isMenuOpen() {
  return document.querySelector('.react-contextmenu--visible') !== null;
}

const keyNames: { [key: string]: string } = {
  ' ': 'Space',
  Escape: 'Esc',
  Delete: 'Del',
  ArrowRight: '→',
  ArrowLeft: '←',
  ArrowUp: '↑',
  ArrowDown: '↓',
};

function keyName(key: string) {
  if (keyNames[key]) {
    return keyNames[key];
  }

  return key.length === 1 ? key.toUpperCase() : key;
}

/** The binding as the overlay shows it: "Ctrl+L", "⌘L", "Shift+→", "g then y". */
export function keyLabel(binding: KeyBinding) {
  const name = keyName(binding.key);

  if (binding.prefix) {
    return `${binding.prefix} then ${binding.key}`;
  }

  const parts = [
    binding.mod ? (isMac ? '⌘' : 'Ctrl') : undefined,
    binding.alt ? (isMac ? '⌥' : 'Alt') : undefined,
    binding.shift ? (isMac ? '⇧' : 'Shift') : undefined,
    name,
  ].filter((x) => x !== undefined);

  return parts.join(isMac ? '' : '+');
}
